import { Suspense, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { Footer, Header, Layout as SvitlofourLayout } from '@svitlofour/ui';
import { Loader } from '@svitlofour/ui';

import { useAuthenticator } from '@aws-amplify/ui-react';
import { Badge, Button, Flex, Layout as AntdLayout } from 'antd';
import { useOpenComplaintsCount, useUserRouter } from 'hooks';

import { ComplaitnsPath } from 'router/routes/constants';

import { Logo } from '../components';

import { getItem } from './helpers';
import { MenuItem } from './types';
import { useResetApiState } from './useResetApiState';

export const Layout = () => {
  const { t } = useTranslation();
  const { role, getRoleRoutesList } = useUserRouter();
  const { user, signOut } = useAuthenticator();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { resetApiState } = useResetApiState();

  const submitSignOut = () => {
    signOut();
    navigate('/');
    resetApiState();
  };

  const routes = useMemo(
    () => (getRoleRoutesList ? getRoleRoutesList() : []),
    [getRoleRoutesList]
  );
  const openComplaintsCount = useOpenComplaintsCount(
    !routes.some(({ path }) => path === ComplaitnsPath)
  );

  // Лічильник відкритих справ — біля назви пункту «Скарги»; у згорнутому до іконок сайдбарі
  // (назви не видно) — на іконці (див. .svt-collapsed-only-badge в index.css).
  const menuItems = useMemo<MenuItem[]>(
    () =>
      routes.map((item) => {
        const isComplaints = item.path === ComplaitnsPath;

        return getItem(
          <NavLink to={item.path}>
            {isComplaints ? (
              <Flex align="center" justify="space-between" gap={8}>
                {t(item.title)}
                <Badge count={openComplaintsCount} size="small" />
              </Flex>
            ) : (
              t(item.title)
            )}
          </NavLink>,
          item.path,
          isComplaints ? (
            <Badge
              className="svt-collapsed-only-badge"
              count={openComplaintsCount}
              size="small"
              offset={[6, 4]}
            >
              {item.icon}
            </Badge>
          ) : (
            item.icon
          )
        );
      }),
    [routes, openComplaintsCount, t]
  );

  return (
    <AntdLayout style={{ height: '100vh', overflow: 'hidden' }}>
      <Header
        logo={<Logo role={role as string} userName={user.attributes?.email} />}
        button={
          <Button ghost type="primary" onClick={submitSignOut}>
            {t('buttons.logout')}
          </Button>
        }
      />

      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        <SvitlofourLayout
          pathname={pathname}
          items={menuItems}
          contentPadding="0 24px 24px"
        >
          <Suspense fallback={<Loader />}>
            <Outlet />
          </Suspense>
          <Footer />
        </SvitlofourLayout>
      </div>
    </AntdLayout>
  );
};
